import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ReportProvider } from './context/ReportContext';
import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ReportPage from './pages/ReportPage';
import LeaderDashboardPage from './pages/LeaderDashboardPage';
import AdminPage from './pages/AdminPage';

/**
 * Protected route that enforces password change on first login
 * Redirects to /change-password if user's password needs to be changed
 */
const ProtectedRouteWithPasswordCheck = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Force password change on first login
  if (user?.requiresPasswordChange) {
    return <Navigate to="/change-password" />;
  }

  return children;
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-blue-600">MyNotifier</h1>
          <div className="flex items-center gap-4">
            <span className="text-gray-600">{user?.full_name}</span>
            <button
              onClick={logout}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-8">
          <h2 className="text-2xl font-bold mb-4">Welcome, {user?.full_name}!</h2>
          
          {user?.team_id && (
            <p className="text-gray-600 mb-6">Team: <strong>{user?.team_name}</strong></p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Report Card */}
            <button
              onClick={() => navigate('/report')}
              className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-lg hover:shadow-lg transition text-left"
            >
              <div className="text-3xl mb-2">📝</div>
              <h3 className="text-lg font-semibold text-blue-900">Submit Report</h3>
              <p className="text-blue-700 text-sm mt-2">Submit your daily Yesterday/Today report</p>
            </button>

            {/* Team Leader Dashboard Card */}
            {(user?.role === 'team_leader' || user?.role === 'admin') && (
              <button
                onClick={() => navigate('/leader-dashboard')}
                className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-lg hover:shadow-lg transition text-left"
              >
                <div className="text-3xl mb-2">👥</div>
                <h3 className="text-lg font-semibold text-green-900">Leader Dashboard</h3>
                <p className="text-green-700 text-sm mt-2">View all team member reports &amp; history</p>
              </button>
            )}

            {user?.role === 'team_member' && (
              <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-lg">
                <div className="text-3xl mb-2">👥</div>
                <h3 className="text-lg font-semibold text-green-900">Team Status</h3>
                <p className="text-green-700 text-sm mt-2">View on the report page</p>
              </div>
            )}

            {/* Admin Settings Card */}
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate('/admin')}
                className="p-6 bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-300 rounded-lg hover:shadow-lg transition text-left"
              >
                <div className="text-3xl mb-2">⚙️</div>
                <h3 className="text-lg font-semibold text-red-900">Admin Settings</h3>
                <p className="text-red-700 text-sm mt-2">Configure system settings &amp; notifications</p>
              </button>
            )}

            {/* Info Card */}
            <div className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 rounded-lg">
              <div className="text-3xl mb-2">ℹ️</div>
              <h3 className="text-lg font-semibold text-purple-900">How It Works</h3>
              <p className="text-purple-700 text-sm mt-2">Submit reports, track team progress, auto-send to Teams</p>
            </div>
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded border border-blue-200">
            <p className="text-blue-900">
              <strong>🚀 Iteration 3:</strong> Team Leader Dashboard is now live!
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ReportProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRouteWithPasswordCheck>
                  <Dashboard />
                </ProtectedRouteWithPasswordCheck>
              }
            />
            <Route
              path="/report"
              element={
                <ProtectedRouteWithPasswordCheck>
                  <ReportPage />
                </ProtectedRouteWithPasswordCheck>
              }
            />
            <Route
              path="/leader-dashboard"
              element={
                <ProtectedRouteWithPasswordCheck>
                  <LeaderDashboardPage />
                </ProtectedRouteWithPasswordCheck>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRouteWithPasswordCheck>
                  <AdminPage />
                </ProtectedRouteWithPasswordCheck>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </ReportProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
